import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import { useParams } from "react-router-dom";
import { CustomButton, CustomLoading } from "@ippon/shared-components";
import { getClientID } from "@/utils/authUtils";
import { parseInvoiceId } from "./billingFormat";
import { fetchInvoicePdfApi } from "./billingApi";

/**
 * 請求書PDFのみを全画面表示するページ。
 * アプリのヘッダー・サイドバー（MainLayout）を挟まず、請求一覧の「PDF表示」から新規タブで開く。
 *
 * PDFは設定管理BE（GET /clients/:clientId/billing/:ym/pdf）が請求ごとに用意したものを表示する。
 * 認証ヘッダーが必要なためURLを直接開かず、API経由で取得した Blob を iframe に渡す。
 * 印刷・ダウンロードはブラウザ標準のPDFビューアの機能をそのまま使う。
 */
const BillingPdfView: React.FC = () => {
  const { id = "" } = useParams();
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 表示中の Object URL。差し替え時と離脱時に解放する（メモリリーク防止）
  const objectUrlRef = useRef<string | null>(null);
  // StrictMode の二重実行や再取得で、古い応答が新しい表示を上書きしないようにする
  const requestIdRef = useRef(0);

  const clientId = getClientID();

  const loadPdf = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    const parsed = parseInvoiceId(id);
    if (!parsed) {
      setError("指定された請求書が見つかりませんでした。");
      setLoading(false);
      return;
    }
    if (!clientId) {
      setError("クライアント情報を取得できませんでした。再度ログインしてください。");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const blob = await fetchInvoicePdfApi(clientId, parsed.ym);
      // 応答の間に再取得が走っていたら、こちらの結果は捨てる
      if (requestId !== requestIdRef.current) return;

      // Content-Type が失われた場合でもブラウザがPDFとして扱えるよう、種別を明示して包み直す
      const url = URL.createObjectURL(
        new Blob([blob], { type: "application/pdf" })
      );
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = url;
      setPdfUrl(url);
      document.title = `請求書 ${parsed.ym}`;
    } catch {
      if (requestId !== requestIdRef.current) return;
      setPdfUrl(null);
      setError("請求書PDFを取得できませんでした。時間をおいて再度お試しください。");
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [clientId, id]);

  useEffect(() => {
    loadPdf();
  }, [loadPdf]);

  // 解放はページを離れるときだけ行う。
  // 取得中の解放は、表示直後のPDFを消してしまうため行わない。
  useEffect(
    () => () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    },
    []
  );

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CustomLoading loading fullScreen={false} size="medium" />
      </Box>
    );
  }

  if (error || !pdfUrl) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          px: 2,
          textAlign: "center",
          color: "#6B7280",
          fontSize: 14,
        }}
      >
        {error ?? "請求書PDFを表示できませんでした。"}
        <CustomButton label="再読み込み" variant="outlined" onClick={loadPdf} />
      </Box>
    );
  }

  return (
    <iframe
      title="請求書"
      src={pdfUrl}
      style={{
        position: "fixed",
        inset: 0,
        width: "100vw",
        height: "100vh",
        border: "none",
      }}
    />
  );
};

export default BillingPdfView;
